import { useCallback, useEffect, useRef, useState } from 'react';
export function useDropdown({ options, value, onChange, searchable = false, disabled = false, }) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const selectedOption = options.find(option => option.value === value);
    const selectedLabel = selectedOption?.label ?? '';
    const filteredOptions = useCallback(() => {
        if (!searchable || !searchTerm) {
            return options;
        }
        return options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [options, searchable, searchTerm]);
    const handleOptionSelect = useCallback((optionValue) => {
        onChange(optionValue);
        setIsDropdownOpen(false);
        setSearchTerm('');
    }, [onChange]);
    const toggleDropdown = useCallback(() => {
        if (disabled)
            return;
        setIsDropdownOpen(prev => !prev);
        if (!isDropdownOpen) {
            setSearchTerm('');
        }
    }, [disabled, isDropdownOpen]);
    const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value);
    }, []);
    const closeDropdown = useCallback(() => {
        setIsDropdownOpen(false);
        setSearchTerm('');
    }, []);
    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;
            const targetElement = target instanceof Element ? target : null;
            if (targetElement?.closest('[data-ibk-outside-click-ignore="true"]'))
                return;
            if (dropdownRef.current && target && !dropdownRef.current.contains(target)) {
                closeDropdown();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [closeDropdown]);
    const currentOptions = filteredOptions();
    const hasOptions = currentOptions.length > 0;
    const handleKeyDown = useCallback((e, optionValue, isOptionDisabled, onSelect) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!isOptionDisabled) {
                onSelect(optionValue);
            }
        }
    }, []);
    return {
        isDropdownOpen,
        searchTerm,
        selectedOption,
        selectedLabel,
        filteredOptions: currentOptions,
        hasOptions,
        toggleDropdown,
        handleOptionSelect,
        handleSearchChange,
        closeDropdown,
        handleKeyDown,
        dropdownRef: dropdownRef,
    };
}
